import React, { useState, useEffect } from 'react';
import { openGoogleCalendar, getAvailableSlots } from '../utils/googleCalendar';

const ScheduleModal = ({ isOpen, onClose }) => {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [appointmentType, setAppointmentType] = useState('consultation');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load available slots when modal opens or appointment type changes
  useEffect(() => {
    if (isOpen) {
      loadAvailableSlots();
    }
  }, [isOpen, appointmentType]);

  const loadAvailableSlots = async () => {
    setLoading(true);
    setSelectedSlot(null); // Reset selection when loading new slots
    try {
      const slots = await getAvailableSlots(appointmentType);
      setAvailableSlots(slots);
    } catch (error) {
      console.error('Error loading available slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSchedule = () => {
    if (!selectedSlot) {
      alert('Please select an appointment time');
      return;
    }

    const appointmentTypes = {
      consultation: {
        title: "Initial Consultation - Dr. Deepika Sinha",
        details: `Initial consultation for aesthetic procedure discussion.\n\nWhat to bring:\n- Medical history\n- List of current medications\n- Photo ID\n- Insurance details (if applicable)\n\nLocation: Dr. Deepika Sinha's Aesthetic Clinic\nAddress: 1st floor, 60, Akriti Ecocity Road, Akriti Ecocity, Bawadiya Kalan, Bhopal\nPhone: +91-9654495307\n\nPlease arrive 15 minutes early for paperwork.`,
        duration: 30
      },
      followup: {
        title: "Follow-up Appointment - Dr. Deepika Sinha", 
        details: `Follow-up appointment for ongoing treatment.\n\nLocation: Dr. Deepika Sinha's Aesthetic Clinic\nAddress: 1st floor, 60, Akriti Ecocity Road, Akriti Ecocity, Bawadiya Kalan, Bhopal\nPhone: +91-9654495307`,
        duration: 15
      },
      procedure: {
        title: "Procedure Appointment - Dr. Deepika Sinha",
        details: `Scheduled procedure appointment.\n\nImportant:\n- Follow pre-procedure instructions\n- Arrange transportation if needed\n- Bring ID and insurance\nDuration will be determined based on specific procedure\n\nLocation: Dr. Deepika Sinha's Aesthetic Clinic\nAddress: 1st floor, 60, Akriti Ecocity Road, Akriti Ecocity, Bawadiya Kalan, Bhopal\nPhone: +91-9654495307`,
        duration: null
      }
    };

    const appointmentDetails = appointmentTypes[appointmentType];
    
    openGoogleCalendar({
      ...appointmentDetails,
      startDate: selectedSlot.datetime
    });

    // Close modal after scheduling
    onClose();
  };

  const handleDirectCall = () => {
    window.location.href = 'tel:+91-9654495307';
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(`Hello! I would like to schedule an appointment with Dr. Deepika Sinha. ${selectedSlot ? `Preferred time: ${selectedSlot.date} at ${selectedSlot.time}` : ''}`);
    window.open(`https://wa.me/919654495307?text=${message}`, '_blank');
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="schedule-modal-content">
        <div className="schedule-modal-header">
          <div className="schedule-header-content">
            <div className="schedule-icon">📅</div>
            <div>
              <h2>Schedule Your Consultation</h2>
              <p>Book an appointment with Dr. Deepika Sinha</p>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="schedule-modal-body">
          <div className="appointment-type-section">
            <h3>Appointment Type</h3>
            <div className="appointment-types">
              <label className={`appointment-type ${appointmentType === 'consultation' ? 'selected' : ''}`}>
                <input 
                  type="radio" 
                  value="consultation" 
                  checked={appointmentType === 'consultation'}
                  onChange={(e) => setAppointmentType(e.target.value)}
                />
                <div className="appointment-info">
                  <strong>Initial Consultation</strong>
                  <span>30 mins - Discussion and evaluation</span>
                </div>
              </label>
              
              <label className={`appointment-type ${appointmentType === 'followup' ? 'selected' : ''}`}>
                <input 
                  type="radio" 
                  value="followup" 
                  checked={appointmentType === 'followup'}
                  onChange={(e) => setAppointmentType(e.target.value)}
                />
                <div className="appointment-info">
                  <strong>Follow-up Visit</strong>
                  <span>15 mins - Progress check</span>
                </div>
              </label>
              
              <label className={`appointment-type ${appointmentType === 'procedure' ? 'selected' : ''}`}>
                <input 
                  type="radio" 
                  value="procedure" 
                  checked={appointmentType === 'procedure'}
                  onChange={(e) => setAppointmentType(e.target.value)}
                />
                <div className="appointment-info">
                  <strong>Procedure Appointment</strong>
                  <span>Duration varies - Treatment session</span>
                </div>
              </label>
            </div>
          </div>

          <div className="time-slots-section">
            <h3>Available Time Slots</h3>
            {loading ? (
              <div className="loading-slots">
                <p>🔄 Checking Dr. Deepika's calendar availability...</p>
              </div>
            ) : availableSlots.length === 0 ? (
              <div className="no-slots">
                <p>No available slots found for {appointmentType}.</p>
                <p>Please call +91-9654495307 for custom scheduling.</p>
              </div>
            ) : (
              <div className="time-slots-grid">
                {availableSlots.map((slot, index) => (
                  <button
                    key={index}
                    className={`time-slot ${selectedSlot === slot ? 'selected' : ''} ${slot.type}`}
                    onClick={() => setSelectedSlot(slot)}
                  >
                    <div className="slot-date">{slot.date}</div>
                    <div className="slot-time">{slot.time}</div>
                    {slot.duration && (
                      <div className="slot-duration">{slot.duration} min</div>
                    )}
                    {!slot.duration && slot.type === 'procedure' && (
                      <div className="slot-duration">Duration varies</div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="schedule-actions">
            <button 
              className="schedule-calendar-btn"
              onClick={handleSchedule}
              // disabled={!selectedSlot}
              disabled={true}
            >
              📅 Calendar widget in progress, Please call/whatsapp
            </button>
            
            <div className="alternative-actions">
              <button className="alt-action-btn" onClick={handleDirectCall}>
                📞 Call Directly
              </button>
              <button className="alt-action-btn" onClick={handleWhatsApp}>
                💬 WhatsApp
              </button>
            </div>
          </div>

          <div className="clinic-info">
            <h4>📍 Clinic Information</h4>
            <p><strong>Shree Aesthetics by Dr. Deepika</strong></p>
            <p>1st floor, 60, Akriti Ecocity Road, Akriti Ecocity, Bawadiya Kalan, Bhopal</p>
            <p>📞 +91-9654495307</p>
            <p><strong>Working Hours:</strong> Tue-Sat 6:00-8:00 PM, Sun 10:00 AM-6:00 PM</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleModal;